import { Button } from "antd";
import i18n from "@/utils/i18n";
import { toast } from "sonner";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ObjectiveType,
  ObjectiveCompletionStatus,
} from "../../../types/client-education-detail.types";
import type {
  ChapterDto,
  ObjectiveDto,
  ObjectiveVideoQuestion,
  VirtualClassroomDto,
} from "../../../types/client-education-detail.types";
import {
  getFileStreamUrl,
  getFileDownloadUrl,
} from "../../../../education-catalog/services/education-catalog.api";
import { clientEducationDetailApi } from "../../../services/client-education-detail.api";
import { JitsiEmbed, extractJitsiUserFromToken } from "./JitsiEmbed";
import { getJitsiAvailability } from "../../../utils/classroom-availability";
import dayjs from "dayjs";
import styles from "./ContentSection.module.scss";

function parseQuestionSeconds(q: ObjectiveVideoQuestion): number {
  if (typeof q.triggerSeconds === "number") return q.triggerSeconds;
  const ts = q.timestamp;
  if (!ts) return 0;
  const parts = ts.split(":").map((p) => parseInt(p, 10));
  if (parts.some((p) => Number.isNaN(p))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] ?? 0;
}

interface Props {
  objective: ObjectiveDto | null;
  chapter?: ChapterDto | null;
  virtualClassrooms?: VirtualClassroomDto[] | null;
  onMarkComplete: (
    objective: ObjectiveDto,
    opts?: { watchedSeconds?: number; lastPositionSeconds?: number }
  ) => Promise<void>;
  onProgress: (
    objective: ObjectiveDto,
    state: { watchedSeconds: number; lastPositionSeconds: number; progressPercent: number },
    opts?: { force?: boolean }
  ) => void | Promise<void>;
  onRequestRefresh?: () => void | Promise<void>;
  onPrev?: () => void;
  onNext?: () => void;
}

export function InlinePlayer(props: Props) {
  const {
    objective,
    chapter,
    virtualClassrooms,
    onMarkComplete,
    onProgress,
    onRequestRefresh,
    onPrev,
    onNext,
  } = props;
  const hasMandatoryQuestions = (objective?.videoQuestions ?? []).some(
    (q) => q.isMandatory,
  );
  const seekUnlocked = (chapter?.progressPercentage ?? 0) >= 100;
  const canSeekFreely =
    seekUnlocked || (objective?.allowSeek !== false && !hasMandatoryQuestions);
  const objectiveId = objective?.id;
  const objectiveType = objective?.type;
  const objectiveLastPositionSeconds = objective?.lastPositionSeconds;
  const objectiveMaxWatchedPosition = objective?.maxWatchedPosition;
  const objectiveUnlockedUntilSeconds = objective?.unlockedUntilSeconds;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const completedIdsRef = useRef<Set<string>>(new Set());
  const [completing, setCompleting] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [streamError, setStreamError] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState(false);
  const maxWatchedRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const SEEK_TOLERANCE = 1.5;
  const [activeQuestion, setActiveQuestion] =
    useState<ObjectiveVideoQuestion | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [answerFeedback, setAnswerFeedback] = useState<
    "correct" | "incorrect" | null
  >(null);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [unlockedUntilSeconds, setUnlockedUntilSeconds] = useState<number | null>(
    objective?.unlockedUntilSeconds ?? null,
  );
  const objectiveProgressRef = useRef({
    lastPositionSeconds: objectiveLastPositionSeconds,
    maxWatchedPosition: objectiveMaxWatchedPosition,
    unlockedUntilSeconds: objectiveUnlockedUntilSeconds,
  });
  objectiveProgressRef.current = {
    lastPositionSeconds: objectiveLastPositionSeconds,
    maxWatchedPosition: objectiveMaxWatchedPosition,
    unlockedUntilSeconds: objectiveUnlockedUntilSeconds,
  };
  const answeredQuestionsRef = useRef<Set<string>>(new Set());
  const closeQuestionModal = (opts?: { answered?: boolean; force?: boolean }) => {
    if (!activeQuestion) return;
    const answered = opts?.answered ?? false;
    const force = opts?.force ?? false;
    if (!answered && activeQuestion.isMandatory && !force) {
      toast.info(i18n.t("inlinePlayer.mandatoryQuestionRequired"));
      return;
    }
    if (answered || !activeQuestion.isMandatory) {
      answeredQuestionsRef.current.add(activeQuestion.id);
    }
    setActiveQuestion(null);
    setAnswerFeedback(null);
    setSelectedOptionId(null);
    void videoRef.current?.play().catch(() => undefined);
  };

  const sortedQuestions = useMemo(() => {
    const qs = (objective?.videoQuestions ?? []).slice();
    return qs
      .map((q) => ({ q, t: parseQuestionSeconds(q) }))
      .sort((a, b) => a.t - b.t);
  }, [objective?.videoQuestions]);

  useEffect(() => {
    answeredQuestionsRef.current = new Set();
    setActiveQuestion(null);
    setSelectedOptionId(null);
    setAnswerFeedback(null);
    setIsSubmittingAnswer(false);
    setUnlockedUntilSeconds(objectiveUnlockedUntilSeconds ?? null);
  }, [objectiveId, objectiveUnlockedUntilSeconds]);

  useEffect(() => {
    if (objectiveType !== ObjectiveType.INTERNAL_FILE) return;
    const video = videoRef.current;
    if (!video) return;
    const progress = objectiveProgressRef.current;
    const initialPosition = progress.lastPositionSeconds ?? 0;
    const seekLimit =
      unlockedUntilSeconds ??
      progress.unlockedUntilSeconds ??
      progress.maxWatchedPosition ??
      initialPosition;
    maxWatchedRef.current = canSeekFreely ? Number.POSITIVE_INFINITY : seekLimit;
    lastTimeRef.current = initialPosition;
    video.currentTime = initialPosition;
  }, [
    objectiveId,
    objectiveType,
    streamUrl,
    canSeekFreely,
    unlockedUntilSeconds,
  ]);

  useEffect(() => {
    if (!objective?.id || objective.type !== ObjectiveType.INTERNAL_FILE) return;
    if (!hasMandatoryQuestions && canSeekFreely) return;
    let cancelled = false;
    clientEducationDetailApi
      .getObjectiveVideoWatch(objective.id)
      .then((progress) => {
        if (cancelled) return;
        const unlocked = progress.unlockedUntilSeconds ?? null;
        setUnlockedUntilSeconds(unlocked);
        const nextLimit =
          unlocked ??
          progress.maxWatchedPosition ??
          progress.lastPositionSeconds ??
          objective.maxWatchedPosition ??
          objective.lastPositionSeconds ??
          0;
        if (!canSeekFreely) {
          maxWatchedRef.current = Math.max(maxWatchedRef.current, nextLimit);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [
    objective?.id,
    objective?.type,
    objective?.maxWatchedPosition,
    objective?.lastPositionSeconds,
    hasMandatoryQuestions,
    canSeekFreely,
  ]);

  useEffect(() => {
    setStreamUrl(null);
    setStreamError(false);
    const fileId = objective?.fileID ?? objective?.file?.id;
    const isVid =
      objective?.type === ObjectiveType.INTERNAL_FILE &&
      (objective?.file?.mimeType?.startsWith("video") ?? false);
    if (!isVid || !fileId) return;
    let revoked = false;
    let created: string | null = null;
    getFileStreamUrl(fileId)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        created = url;
        setStreamUrl(url);
      })
      .catch(() => setStreamError(true));
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [objective?.id, objective?.fileID, objective?.file?.id, objective?.type, objective?.file?.mimeType]);

  useEffect(() => {
    setDownloadUrl(null);
    setDownloadError(false);
    const fileId = objective?.fileID ?? objective?.file?.id;
    const mime = objective?.file?.mimeType ?? "";
    const isFile = objective?.type === ObjectiveType.INTERNAL_FILE;
    const isDownloadable = isFile && !mime.startsWith("video");
    if (!isDownloadable || !fileId) return;
    let cancelled = false;
    getFileDownloadUrl(fileId)
      .then((url) => {
        if (!cancelled) setDownloadUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDownloadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [objective?.id, objective?.fileID, objective?.file?.id, objective?.type, objective?.file?.mimeType]);

  useEffect(() => {
    // Sekme gizlendiğinde durdur (tab switch)
    function handleVisibilityChange() {
      if (document.hidden) {
        const v = videoRef.current;
        if (v && !v.paused) v.pause();
      }
    }
    // Pencere odak kaybettiğinde durdur (alt-tab, başka uygulama).
    // window.blur document.hidden kontrolü yapmaz çünkü Chrome'da blur
    // sırasında hidden=false olur; burada koşulsuz pause gerekiyor.
    function handleWindowBlur() {
      const v = videoRef.current;
      if (v && !v.paused) v.pause();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, []);

  if (!objective) {
    return (
      <div className={styles.playerEmpty}>
        Bir içerik seç.
      </div>
    );
  }

  const isVideo =
    objective.type === ObjectiveType.INTERNAL_FILE &&
    (objective.file?.mimeType?.startsWith("video") ?? false);
  const isDownloadableFile =
    objective.type === ObjectiveType.INTERNAL_FILE && !isVideo;
  const fileName = objective.file?.originalName ?? "";
  const mimeType = objective.file?.mimeType ?? "";
  const lowerName = fileName.toLowerCase();
  const isPdf =
    isDownloadableFile &&
    (mimeType.includes("pdf") || lowerName.endsWith(".pdf"));
  const isOfficeDoc =
    isDownloadableFile &&
    !isPdf &&
    (mimeType.includes("officedocument") ||
      mimeType.includes("msword") ||
      mimeType.includes("ms-excel") ||
      mimeType.includes("ms-powerpoint") ||
      /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/i.test(lowerName));
  const officeViewerUrl =
    isOfficeDoc && downloadUrl
      ? `https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`
      : null;
  const previewUrl = isPdf
    ? downloadUrl
    : isOfficeDoc
      ? officeViewerUrl
      : downloadUrl;
  const isLink = objective.type === ObjectiveType.LINK;
  const isJitsi = objective.type === ObjectiveType.JITSI;
  const jitsiAvailability = isJitsi
    ? getJitsiAvailability(virtualClassrooms)
    : null;

  function handleTimeUpdate(e: React.SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (!v.duration) return;
    const previousTime = lastTimeRef.current;
    if (!canSeekFreely) {
      if (v.seeking) {
        if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
          v.currentTime = maxWatchedRef.current;
        }
      } else {
        const delta = v.currentTime - previousTime;
        if (delta >= 0 && delta <= SEEK_TOLERANCE) {
          if (v.currentTime > maxWatchedRef.current) {
            maxWatchedRef.current = v.currentTime;
          }
        } else if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
          v.currentTime = maxWatchedRef.current;
        }
      }
    } else if (v.currentTime > maxWatchedRef.current) {
      maxWatchedRef.current = v.currentTime;
    }
    lastTimeRef.current = v.currentTime;

    if (!activeQuestion && !v.paused) {
      if (!seekUnlocked) {
        const trigger = sortedQuestions.find(
          ({ q, t }) =>
            previousTime < t &&
            v.currentTime >= t &&
            v.currentTime < t + 1.5 &&
            !answeredQuestionsRef.current.has(q.id),
        );
        if (trigger) {
          v.pause();
          if (document.fullscreenElement) {
            void document.exitFullscreen().catch(() => undefined);
          }
          setActiveQuestion(trigger.q);
          setSelectedOptionId(null);
          setAnswerFeedback(null);
          return;
        }
      }
    }
    const progressPercent = Math.min(100, Math.round((v.currentTime / v.duration) * 100));
    const result = onProgress(objective as ObjectiveDto, {
      watchedSeconds: Math.floor(v.currentTime),
      lastPositionSeconds: Math.floor(v.currentTime),
      progressPercent,
    });
    if (result && typeof (result as Promise<void>).catch === "function") {
      void (result as Promise<void>).catch(() => {
        v.currentTime = Math.min(v.currentTime, maxWatchedRef.current);
      });
    }
  }

  async function submitAnswer() {
    if (!objective || !activeQuestion || !selectedOptionId) return;
    setIsSubmittingAnswer(true);
    try {
      const res = await clientEducationDetailApi.answerObjectiveVideoQuestion(
        objective.id,
        {
          questionID: activeQuestion.id,
          selectedOptionID: selectedOptionId,
        },
      );

      if (!res.isCorrect) {
        setAnswerFeedback("incorrect");
        toast.error(
          i18n.t(
            activeQuestion.isMandatory
              ? "inlinePlayer.mandatoryQuestionIncorrect"
              : "inlinePlayer.incorrectTryAgain",
          ),
        );
        closeQuestionModal({
          answered: !activeQuestion.isMandatory,
          force: true,
        });
        return;
      }

      setAnswerFeedback("correct");
      toast.success(i18n.t("inlinePlayer.correctContinue"));
      answeredQuestionsRef.current.add(activeQuestion.id);
      if (typeof res.unlockedUntilSeconds === "number") {
        setUnlockedUntilSeconds(res.unlockedUntilSeconds);
        maxWatchedRef.current = Math.max(
          maxWatchedRef.current,
          res.unlockedUntilSeconds,
        );
      }
      window.setTimeout(() => {
        closeQuestionModal({ answered: true });
      }, 700);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      if (message === "errors.OBJECTIVE_VIDEO_MANDATORY_QUESTION_LOCKED") {
        toast.info(i18n.t("educationVideoPlayer.seekDisabled"));
      } else if (message === "errors.OBJECTIVE_VIDEO_QUESTION_NOT_REACHED") {
        toast.error(i18n.t("inlinePlayer.questionNotReached"));
      } else {
        toast.error(i18n.t("inlinePlayer.answerSubmitError"));
      }
    } finally {
      setIsSubmittingAnswer(false);
    }
  }

  function handleSeeking(e: React.SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    if (canSeekFreely) return;
    if (v.currentTime > maxWatchedRef.current + SEEK_TOLERANCE) {
      v.currentTime = maxWatchedRef.current;
    }
  }

  async function handleEnded() {
    if (!objective) return;
    const v = videoRef.current;
    const duration = Math.floor(v?.duration ?? 0);
    const alreadyCompleted =
      (objective.completionStatus ?? objective.status) === ObjectiveCompletionStatus.COMPLETED ||
      completedIdsRef.current.has(objective.id);
    if (!alreadyCompleted && duration > 0) {
      // Video bitti: /video/watch endpoint'ine son bir delta gönderip
      // backend'in endGap (varsayılan 15 sn) toleransıyla otomatik tamamlamasını sağla.
      // Manuel /complete endpoint'i video objective'leri için OBJECTIVE_VIDEO_USE_VIDEO_WATCH
      // hatası fırlatıyor; bu yüzden asla çağırmıyoruz.
      completedIdsRef.current.add(objective.id);
      onProgress(
        objective,
        {
          watchedSeconds: duration,
          lastPositionSeconds: duration,
          progressPercent: 100,
        },
        { force: true },
      );
      maxWatchedRef.current = Number.POSITIVE_INFINITY;
    }
    if (onRequestRefresh) {
      await onRequestRefresh();
    }
  }

  return (
    <div className={styles.player}>
      <div className={styles.playerHeader}>
        <h3>{objective.title}</h3>
        <div className={styles.playerNav}>
          <Button onClick={onPrev} disabled={!onPrev}>← {i18n.t("common.previous")}</Button>
          <Button onClick={onNext} disabled={!onNext}>Sonraki →</Button>
        </div>
      </div>

      {isVideo && streamUrl && (
        <>
          <video
            ref={videoRef}
            src={streamUrl}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            className={styles.playerVideo}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onSeeked={handleSeeking}
            onEnded={handleEnded}
          />
          {objective.allowSeek === false && !seekUnlocked && (
            <div style={{ marginTop: 8, fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
              {i18n.t("educationVideoPlayer.seekDisabled")}
            </div>
          )}
        </>
      )}

      {isVideo && !streamUrl && !streamError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.videoLoading")}</div>
      )}

      {isVideo && streamError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.videoLoadError")}</div>
      )}

      {isDownloadableFile && downloadUrl && (
        <>
          <iframe
            title={objective.title}
            src={previewUrl ?? downloadUrl}
            className={styles.playerPdf}
          />
          <div style={{ marginTop: 8 }}>
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              <Button>
                {isPdf
                  ? "PDF'i indir"
                  : isOfficeDoc
                    ? i18n.t("inlinePlayer.downloadDocument")
                    : i18n.t("inlinePlayer.downloadFile")}
              </Button>
            </a>
          </div>
        </>
      )}

      {isDownloadableFile && !downloadUrl && !downloadError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.fileLoading")}</div>
      )}

      {isDownloadableFile && downloadError && (
        <div className={styles.playerEmpty}>{i18n.t("inlinePlayer.fileLoadError")}</div>
      )}

      {isLink && objective.externalUrl && (
        <div className={styles.playerLink}>
          <p>{i18n.t("inlinePlayer.externalLink")}</p>
          <a href={objective.externalUrl} target="_blank" rel="noreferrer">
            <Button type="primary">{i18n.t("inlinePlayer.openLink")}</Button>
          </a>
        </div>
      )}

      {isJitsi && objective.externalUrl && jitsiAvailability?.canJoin && (
        <JitsiEmbed
          externalUrl={objective.externalUrl}
          token={objective.token}
          user={extractJitsiUserFromToken(objective.token)}
          className={styles.playerJitsi}
        />
      )}

      {isJitsi && objective.externalUrl && !jitsiAvailability?.canJoin && (
        <div className={styles.playerLink}>
          <p>
            {jitsiAvailability?.nextStart != null
              ? i18n.t("inlinePlayer.jitsiNotStarted", {
                  when: dayjs(jitsiAvailability.nextStart).format(
                    "DD MMM YYYY HH:mm",
                  ),
                })
              : i18n.t("inlinePlayer.jitsiUnscheduled")}
          </p>
        </div>
      )}

      {isJitsi && !objective.externalUrl && (
        <div className={styles.playerLink}>
          <p>{i18n.t("inlinePlayer.jitsiNotReady")}</p>
        </div>
      )}

      {/* Canlı (Jitsi) oturumlarda manuel "tamamla" yok — tamamlanma katılım/
          yoklama üzerinden işlenir; kursiyer bu butonla kendini tamamlayamamalı. */}
      {(objective.completionStatus ?? objective.status) !== ObjectiveCompletionStatus.COMPLETED && !isVideo && !isJitsi && (
        <div style={{ marginTop: 12 }}>
          <Button
            type="primary"
            loading={completing}
            onClick={async () => {
              setCompleting(true);
              try {
                await onMarkComplete(objective);
              } finally {
                setCompleting(false);
              }
            }}
          >
            Tamamlandı işaretle
          </Button>
        </div>
      )}

      {activeQuestion && (
        <div
          className={styles.questionOverlay}
          role="dialog"
          aria-modal="true"
          onClick={() => closeQuestionModal()}
        >
          <div
            className={styles.questionCard}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={styles.questionClose}
              onClick={() => closeQuestionModal()}
              aria-label={i18n.t("common.close")}
            >
              ×
            </button>
            <h4>
              {activeQuestion.questionText ?? activeQuestion.question}
              {activeQuestion.isMandatory && (
                <span className={styles.questionRequired}>*</span>
              )}
            </h4>
            <div className={styles.questionOptions}>
              {(activeQuestion.options ?? []).map((opt) => {
                const isSelected = selectedOptionId === opt.id;
                return (
                  <label
                    key={opt.id}
                    className={`${styles.questionOption} ${
                      isSelected ? styles.questionOptionSelected : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="video-question"
                      checked={isSelected}
                      onChange={() => {
                        setSelectedOptionId(opt.id);
                        setAnswerFeedback(null);
                      }}
                    />
                    <span>{opt.optionText ?? opt.text}</span>
                  </label>
                );
              })}
            </div>
            {answerFeedback === "incorrect" && (
              <p className={styles.questionError}>
                {activeQuestion.isMandatory
                  ? i18n.t("inlinePlayer.mandatoryQuestionIncorrect")
                  : i18n.t("inlinePlayer.incorrectTryAgain")}
              </p>
            )}
            {answerFeedback === "correct" && (
              <p className={styles.questionOk}>{i18n.t("inlinePlayer.correctContinue")}</p>
            )}
            <div className={styles.questionActions}>
              <button
                type="button"
                className={styles.questionSubmitButton}
                disabled={
                  !selectedOptionId ||
                  answerFeedback === "correct" ||
                  isSubmittingAnswer
                }
                onClick={submitAnswer}
              >
                {isSubmittingAnswer
                  ? i18n.t("common.loading")
                  : i18n.t("educationCatalog.videoQuestion.submitAnswer")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
